import { Plus, X } from "lucide-react";
import { FORMAT_OPTIONS, KNOCKOUT_SIZES, DATA_SOURCE_OPTIONS } from "@/components/tournament-setup/constants";

// iter86.39 — Extracted from pages/TournamentSetup.jsx (tech-debt budget).
// Renders every field that only appears when template === "custom":
// data-feed picker, format, bracket size, manual team entry, date range.
export function CustomTournamentFields({
    league,
    customFeaturesDisabled = [], portfolioUnlock, setPortfolioUnlock,
    dataSource, onSelectDataSource, termsAcceptedFor,
    format, onFormatChange,
    knockoutSize, setKnockoutSize,
    customTeams, updateTeam, addTeamRow, removeTeamRow,
    startDate, setStartDate, endDate, setEndDate, dateRangeDays,
}) {
    return (
        <>
            {/* iter84.4 (Phase 2) — Custom Tournament Data-Feed
                Wizard. Each non-manual feed requires (1) the
                site-admin per-league entitlement flag to be ON
                and (2) the rivalry admin to explicitly accept the
                data-use terms via the inline confirmation modal.
                Manual entry is always available and is the
                default. */}
            <div data-testid="custom-data-source-section">
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                    Data feed
                </label>
                <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                    How will scores and schedule reach this tournament? You can't change this after setup —
                    each source maps to a different card under the <strong>Data Feed</strong> tab in admin.
                    Manual entry is the default. <strong className="text-cyber">Picking a feed enables it for this rivalry automatically</strong>
                    {" "}(the options shown are governed by your site admin's global settings) and requires you to accept the data-use terms below before submitting.
                </p>
                <div className="space-y-2">
                    {DATA_SOURCE_OPTIONS.filter(o => {
                        // iter86.74 — Availability is governed globally by the
                        // site admin (Custom Template Features). Feeds the
                        // rivalry already enabled stay visible either way.
                        // Manual is always available.
                        if (o.key === "manual") return true;
                        if (o.key === "gotsport") return !customFeaturesDisabled.includes("gotsport_enabled") || !!league?.gotsport_enabled;
                        if (o.key === "csv") return !customFeaturesDisabled.includes("feed_csv_enabled") || !!league?.feed_csv_enabled;
                        return true;
                    }).map(o => {
                        // After the filter every option is entitled.
                        const selected = dataSource === o.key;
                        const needsTerms = o.key !== "manual";
                        return (
                            <label
                                key={o.key}
                                data-testid={`data-source-${o.key}-label`}
                                className={`flex items-start gap-3 p-3 border-2 rounded-md transition-all ${
                                    selected
                                        ? "border-cyber bg-cyber/10 cursor-pointer"
                                        : "border-[#333] bg-ink hover:border-[#555] cursor-pointer"
                                }`}
                            >
                                <input
                                    data-testid={`data-source-${o.key}-radio`}
                                    type="radio"
                                    name="dataSource"
                                    checked={selected}
                                    onChange={() => onSelectDataSource(o.key, needsTerms)}
                                    className="mt-1 w-4 h-4 accent-cyber"
                                />
                                <span className="text-sm flex-1">
                                    <span className="font-bold text-white flex items-center gap-2 flex-wrap">
                                        <span aria-hidden="true">{o.icon}</span>
                                        {o.label}
                                        {needsTerms && termsAcceptedFor[o.key] && (
                                            <span className="pill text-[9px] py-0.5 border-cyber text-cyber font-normal uppercase tracking-wider" data-testid={`data-source-${o.key}-terms-accepted`}>
                                                Terms accepted
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-zinc-400 leading-relaxed block mt-0.5">
                                        {o.desc}
                                    </span>
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* iter86.74 — Portfolio Draft feature toggle (league admin
                self-selects; availability governed by the site admin's
                Custom Template Features card). */}
            {(!customFeaturesDisabled.includes("portfolio_mode_unlocked") || !!league?.portfolio_mode_unlocked) && (
                <div data-testid="custom-features-section">
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                        Features
                    </label>
                    <label
                        data-testid="custom-portfolio-draft-label"
                        className={`flex items-start gap-3 p-3 border-2 rounded-md transition-all cursor-pointer ${
                            portfolioUnlock ? "border-cyber bg-cyber/10" : "border-[#333] bg-ink hover:border-[#555]"
                        }`}
                    >
                        <input
                            data-testid="custom-portfolio-draft-checkbox"
                            type="checkbox"
                            checked={!!portfolioUnlock}
                            onChange={(e) => setPortfolioUnlock(e.target.checked)}
                            className="mt-1 w-4 h-4 accent-cyber"
                        />
                        <span className="text-sm flex-1">
                            <span className="font-bold text-white flex items-center gap-2">
                                <span aria-hidden="true">💼</span> Portfolio Draft
                            </span>
                            <span className="text-xs text-zinc-400 leading-relaxed block mt-0.5">
                                Unlocks the portfolio-style draft mode for this rivalry (applies rivalry-wide, not just this tournament).
                            </span>
                        </span>
                    </label>
                </div>
            )}

            <div data-testid="custom-format-section">
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Format</label>
                <div className="space-y-2">
                    {FORMAT_OPTIONS.map(f => (
                        <label
                            key={f.key}
                            data-testid={`format-${f.key}-label`}
                            className={`flex items-start gap-3 cursor-pointer p-3 border-2 rounded-md transition-all ${
                                format === f.key
                                    ? "border-cyber bg-cyber/10"
                                    : "border-[#333] bg-ink hover:border-[#555]"
                            }`}
                        >
                            <input
                                data-testid={`format-${f.key}-radio`}
                                type="radio"
                                name="format"
                                checked={format === f.key}
                                onChange={() => onFormatChange(f.key)}
                                className="mt-1 w-4 h-4 accent-cyber cursor-pointer"
                            />
                            <span className="text-sm">
                                <span className="font-bold text-white block">{f.label}</span>
                                <span className="text-xs text-zinc-400 leading-relaxed">{f.desc}</span>
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {format === "knockout_only" && (
                <div data-testid="knockout-size-section">
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Bracket size</label>
                    <div className="flex flex-wrap gap-2">
                        {KNOCKOUT_SIZES.map(s => (
                            <button
                                key={s}
                                type="button"
                                data-testid={`knockout-size-${s}-btn`}
                                onClick={() => setKnockoutSize(s)}
                                className={`brutal-btn ${knockoutSize === s ? "bg-cyber text-black" : "bg-transparent border-[#333] shadow-none text-white"} px-4 py-2`}
                            >
                                {s}-team
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                        Seed order = the order you enter teams below. Top seed plays bottom seed first. <b className="text-cyber">Have fewer teams than your bracket size?</b> Top seeds will automatically receive byes into the next round (you need at least {Math.floor(knockoutSize / 2) + 1} teams).
                    </p>
                    {(() => {
                        const filled = customTeams.filter(t => t.name.trim()).length;
                        if (filled === 0 || filled >= knockoutSize) return null;
                        const byes = knockoutSize - filled;
                        const byeSeedsLabel = Array.from({ length: byes }, (_, i) => `Seed ${i + 1}`).join(", ");
                        return (
                            <div data-testid="bye-preview-chip" className="brutal-card-light p-3 mt-3 text-xs flex items-start gap-2 border-cyber/40">
                                <span className="text-cyber font-bold uppercase tracking-widest text-[10px] shrink-0">{byes} byes</span>
                                <span className="text-zinc-300">
                                    <b className="text-cyber">{byeSeedsLabel}</b> will automatically advance into round 2 (no first-round match). Lowest-ranked remaining seeds will play highest-ranked seeds without byes.
                                </span>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* iter84.9 — Teams entry visible ONLY when data
                source = manual. For GotSport/ESPN/CSV the teams
                sync in via the Data Feed tab after creation, so
                asking the admin to type them now would be
                redundant and risk name mismatches. */}
            {dataSource === "manual" && (
            <div>
                <div className="flex items-center justify-between mb-3">
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500">Teams</label>
                    <span className="text-xs text-zinc-500" data-testid="team-count-pill">{customTeams.filter(t => t.name.trim()).length} added</span>
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                    {customTeams.map((t, i) => (
                        <div key={`team-row-${i}`} className="flex gap-2 items-center">
                            {format === "knockout_only" && (
                                <span className="w-8 text-xs text-zinc-500 font-display font-extrabold text-center" data-testid={`seed-num-${i}`}>#{i + 1}</span>
                            )}
                            <input
                                data-testid={`custom-team-name-${i}`}
                                placeholder={format === "knockout_only" ? `Seed ${i + 1} team` : "Team name"}
                                value={t.name}
                                onChange={(e) => updateTeam(i, "name", e.target.value)}
                                className="brutal-input flex-1"
                            />
                            {format === "group_knockout" && (
                                <input
                                    data-testid={`custom-team-group-${i}`}
                                    placeholder="Group (A, B, …)"
                                    value={t.group}
                                    onChange={(e) => updateTeam(i, "group", e.target.value.toUpperCase().slice(0, 2))}
                                    className="brutal-input w-24"
                                />
                            )}
                            {/* iter31o — optional seed input for non-knockout formats.
                                For knockout_only the seed is the row number, shown to the left. */}
                            {format !== "knockout_only" && (
                                <input
                                    data-testid={`custom-team-seed-${i}`}
                                    placeholder="Seed"
                                    type="number"
                                    min={1}
                                    value={t.seed ?? ""}
                                    onChange={(e) => updateTeam(i, "seed", e.target.value)}
                                    className="brutal-input w-20"
                                    title="Optional. Equal seeds share a tier in auto-draft balancing (lower = better)."
                                />
                            )}
                            <button
                                data-testid={`remove-team-${i}-btn`}
                                onClick={() => removeTeamRow(i)}
                                className="brutal-btn-ghost px-3"
                                type="button"
                                disabled={customTeams.length <= 2}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
                <button
                    data-testid="add-custom-team-btn"
                    type="button"
                    onClick={addTeamRow}
                    className="brutal-btn-ghost mt-3 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add team
                </button>
            </div>
            )}
            {/* iter84.9 — Friendly note when a non-manual feed is
                chosen so the admin knows where their teams will
                come from instead of expecting an entry field. */}
            {dataSource !== "manual" && (
                <div className="brutal-card-light p-3 text-xs text-zinc-400 leading-relaxed" data-testid="teams-sync-note">
                    <strong className="text-cyber">Teams will sync from your Data Feed.</strong>{" "}
                    After creation, head to <strong>Tournament Admin → Data Feed</strong> to wire up the {dataSource === "gotsport" ? "GotSport event" : dataSource === "csv" ? "first CSV upload" : "data source"}; teams + schedule import automatically and you can rename anything that doesn't match your roster.
                </div>
            )}
            {/* iter84.9 — Tournament dates required for custom
                tournaments. Drives the GotSport auto-sync poller
                window + warns when admin picks a >30-day range. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tournament-dates-section">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                        Start date
                    </label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        data-testid="tournament-start-date"
                        className="brutal-input w-full"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                        End date
                    </label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        data-testid="tournament-end-date"
                        className="brutal-input w-full"
                    />
                </div>
                {dateRangeDays > 30 && (
                    <p className="sm:col-span-2 text-xs text-blaze leading-relaxed" data-testid="tournament-dates-long-warning">
                        ⚠️ Your tournament spans <strong>{dateRangeDays} days</strong>. Confirm this is correct — auto-sync runs the entire window, so a long range can rack up requests against your data source.
                    </p>
                )}
            </div>
        </>
    );
}
